import { useQuery } from '@tanstack/react-query';
import { IGetChartData, IGetChartDataResult, ReportParameterTypes } from '../logic/api';
import { useContext, useRef } from 'react';
import { SessionContext } from '../utility/useSession';
import { IChartOptions, isChartOptions } from '../components/charts/common/chart-options';
import { UserParams } from '../context/user-params-context';

type ChartParameter = {
  type: ReportParameterTypes;
  name: string;
};

interface UseChartDataOptions<
  TData extends unknown[] = unknown[],
  TOptions extends IChartOptions = IChartOptions
> {
  /**
   * When true, API calls will be made even with empty parameters or userParams
   * When false (default), API calls require non-empty parameters and userParams
   */
  allowEmptyParams?: boolean;

  /**
   * Validates chart options structure
   * Default: isChartOptions
   */
  validateOptions?: (options: unknown) => options is TOptions;

  /**
   * Validates query result data
   */
  validateData?: (data: unknown) => data is TData;
}

// Shape of the data returned by the useChartData hook
type ChartDataResult<TData, TOptions> = {
  data: TData | null;
  options: TOptions | null;
  name: string | null;
  translationKey: string | null;
};

const createUserParamsCacheKey = (params?: UserParams) => {
  if (!params) return undefined;

  return {
    startDate: params['@u_startDate'] || null,
    endDate: params['@u_endDate'] || null,
    driverIds: params['@u_driverIds']?.join(',') || null,
    receptionistIds: params['@u_receptionistIds']?.join(',') || null,
    interval: params['@u_interval'] || null,
    chartInterval: params['@u_chartInterval'] || null,
    daysToSell: params['@u_daysToSell'] || null,
  };
};

/**
 * Hook to fetch and transform chart data with validation and caching
 */
export function useChartData<
  TData extends unknown[] = unknown[],
  TOptions extends IChartOptions = IChartOptions
>(
  uuid?: string,
  parameters?: ChartParameter[],
  userParams?: UserParams,
  options?: UseChartDataOptions<TData, TOptions>
) {
  const session = useContext(SessionContext);
  // Track request to support cancellation of in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    // allowEmptyParams = false,
    validateOptions = isChartOptions as (options: unknown) => options is TOptions,
    validateData,
  } = options || {};

  const fetchChartData = async (): Promise<IGetChartDataResult | null> => {
    abortControllerRef.current = new AbortController();

    if (!uuid) {
      return null;
    }

    const payload: IGetChartData = {
      UUID: uuid,
      parameters: parameters || [],
      values: userParams || {},
    };

    const baseUrl = session.current.context.url || 'http://localhost:3001/api';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (session.current.context.headers?.authorization) {
      headers['Authorization'] = session.current.context.headers.authorization;
    }

    const chartApiUrl = `${baseUrl}/chart/getChartData`;

    const response = await fetch(chartApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  };

  return useQuery<IGetChartDataResult | null, Error, ChartDataResult<TData, TOptions>>({
    queryKey: ['chartData', uuid, createUserParamsCacheKey(userParams)],
    queryFn: () => fetchChartData(),
    enabled: !!uuid, // TODO: think of a better way to handle no parameters
    select: (result) => {
      if (!result) {
        return { data: null, options: null, name: null, translationKey: null };
      }

      // Validate chart options
      let validatedOptions: TOptions | null = null;
      if (result.options) {
        if (validateOptions(result.options)) {
          validatedOptions = result.options as TOptions;
        } else {
          console.warn(`Invalid options received for chart ${uuid}`);
        }
      }

      // Validate chart data
      let validatedData: TData | null = null;
      if (result.queryResult) {
        if (!validateData || validateData(result.queryResult)) {
          validatedData = result.queryResult as TData;
        } else {
          console.warn(`Invalid data structure received for chart ${uuid}`);
        }
      }

      return {
        data: validatedData,
        options: validatedOptions,
        name: result.name || null,
        translationKey: (result as any).translationKey ?? null, // assumes backend now returns this
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
